import ChevronLeftIcon from "@mui/icons-material/ChevronLeft"
import ChevronRightIcon from "@mui/icons-material/ChevronRight"
import { Box, CSSObject, Drawer, Fab, Theme, useTheme } from "@mui/material"
import { SetStateAction, useContext, useEffect } from "react"
import { customPalette } from "../../../../../../../../theme"
import { BOTTOM_NAVIGATION_HEIGHT } from "../../../../../../../components/buttons/navigate-button/CustomBottomNavigation"
import { IconCardInfo } from "../../../../../../../components/custom-icons/icon-card-info"
import { IconParcelleProject } from "../../../../../../../components/custom-icons/icon-parcelle-project"
import { TabEnum } from "../../../../../../../components/tabs/tabs"
import { BSBuildingTabMenu } from "../../building/components/BSBuildingTabMenu"
import { BSBuildingContext } from "../../building/components/context/BSBuildingContext"
import { BSVariantControlPanelContext } from "../BSVariantControlPanelContext"
import { ControlTabPanel } from "../ControlPanelTab"
import { WarningUpdatedDialogContext } from "../Dialog/WarningUpdatedDialogContext"

interface IProps {
  openVariantSideNavBar: boolean
  setOpenVariantSideNavBar: React.Dispatch<SetStateAction<boolean>>
  variantDrawerWidth: number
  setVariantDrawerWidth: React.Dispatch<SetStateAction<number>>
}

const openedMixin = (theme: Theme, drawerWidth: number, backgroundColor: string): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
  background: backgroundColor,
  scrollbarWidth: "none",
  boxShadow: theme.shadows[5],
  borderRadius: theme.shape.borderRadius,
  height: `calc(100% - ${BOTTOM_NAVIGATION_HEIGHT} -  2vh)`,
  margin: `${theme.spacing(10)} ${theme.spacing(1)} ${theme.spacing(1)} ${theme.spacing(1)}`,
  padding: "2px",
  zIndex: 1200,
})

const closedMixin = (theme: Theme, drawerWidth: number, backgroundColor: string): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  background: backgroundColor,
  width: drawerWidth,
  scrollbarWidth: "none",
  boxShadow: theme.shadows[5],
  borderRadius: theme.shape.borderRadius,
  padding: "2px",
  height: `calc(100% - ${BOTTOM_NAVIGATION_HEIGHT} -  2vh)`,
  margin: `${theme.spacing(10)} ${theme.spacing(1)} ${theme.spacing(1)} ${theme.spacing(1)}`,
  zIndex: 1200,
})

export const openedFabMixin = (theme: Theme, backgroundColor: string): CSSObject => ({
  transition: theme.transitions.create("left", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
  position: "fixed",
  background: backgroundColor,
  zIndex: 1300,
})

export const closedFabMixin = (theme: Theme, backgroundColor: string): CSSObject => ({
  transition: theme.transitions.create("left", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  background: backgroundColor,
  position: "fixed",
  zIndex: 1300,
})

export function BSVariantSideNavBar({
  openVariantSideNavBar,
  setOpenVariantSideNavBar,
  variantDrawerWidth,
  setVariantDrawerWidth,
}: Readonly<IProps>): React.JSX.Element {
  const theme = useTheme()

  const { controlPanelRef } = useContext(BSVariantControlPanelContext)
  const { selectBSBuilding } = useContext(BSBuildingContext)
  const { selectedTab, handleSelectTab } = useContext(WarningUpdatedDialogContext)

  useEffect(() => {
    if (openVariantSideNavBar) {
      setVariantDrawerWidth(340)
    }
  }, [openVariantSideNavBar, setVariantDrawerWidth])

  useEffect(() => {
    if (!openVariantSideNavBar) {
      setVariantDrawerWidth(80)
    }
  }, [openVariantSideNavBar, setVariantDrawerWidth])

  return (
    <>
      <Fab
        color="primary"
        onClick={() => setOpenVariantSideNavBar(!openVariantSideNavBar)}
        sx={{
          position: "absolute",
          top: 110,
          left: openVariantSideNavBar ? 335 : 75,
          zIndex: 1250,
          width: 25,
          height: 25,
          minHeight: "unset",
          lineHeight: "normal",
          padding: 0,
          border: 2,
          borderColor: customPalette.white,
          ...(openVariantSideNavBar
            ? {
                ...openedFabMixin(theme, customPalette.lightGray6),
                "& .MuiDrawer-paper": openedFabMixin(theme, customPalette.lightGray6),
              }
            : {
                ...closedFabMixin(theme, customPalette.lightGray6),
                "& .MuiDrawer-paper": closedFabMixin(theme, customPalette.lightGray6),
              }),
        }}>
        {openVariantSideNavBar ? (
          <ChevronLeftIcon sx={{ color: customPalette.dark1, width: 20, height: 20 }} />
        ) : (
          <ChevronRightIcon sx={{ color: customPalette.dark1, width: 20, height: 20 }} />
        )}
      </Fab>
      <Drawer
        variant="permanent"
        sx={{
          width: variantDrawerWidth,
          flexShrink: 0,
          whiteSpace: "nowrap",
          boxSizing: "border-box",
          overflowX: "hidden",

          ...(openVariantSideNavBar
            ? {
                "& .MuiDrawer-paper": openedMixin(theme, variantDrawerWidth, customPalette.lightGray7),
                height: 0,
              }
            : {
                "& .MuiDrawer-paper": closedMixin(theme, variantDrawerWidth, customPalette.lightGray7),
                height: 0,
              }),
        }}>
        <Box
          ref={controlPanelRef}
          border={4}
          display="flex"
          flexDirection="column"
          height="100%"
          borderColor={customPalette.lightGray7}
          borderRadius={5}
          gap={1}>
          <ControlTabPanel
            open={openVariantSideNavBar}
            title="Informations projet"
            icon={IconCardInfo}
            isSelectedTab={selectedTab === TabEnum.BS_VARIANT_INFO}
            onClick={() => {
              selectBSBuilding("")
              handleSelectTab(TabEnum.BS_VARIANT_INFO)
            }}
          />

          <ControlTabPanel
            open={openVariantSideNavBar}
            title="Parcelle"
            icon={IconParcelleProject}
            isSelectedTab={selectedTab === TabEnum.BS_VARIANT_PARCELLE}
            onClick={() => {
              selectBSBuilding("")
              handleSelectTab(TabEnum.BS_VARIANT_PARCELLE)
            }}
          />

          <BSBuildingTabMenu open={openVariantSideNavBar} handleSelectTab={handleSelectTab} selectedTab={selectedTab} />
        </Box>
      </Drawer>
    </>
  )
}
